import { isMobileBreakpoint } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

/** @type {Map<string, string>} Shared across all instances — one fetch per URL */
const recCache = new Map();

const log = (...args) => console.debug('[cart-upsell]', ...args);

class CartUpsell extends HTMLElement {
  /** @type {AbortController | null} */
  #activeFetch = null;

  static get observedAttributes() {
    return ['data-product-id'];
  }

  connectedCallback() {
    log('connected', { variant: this.className, productId: this.dataset.productId });
    this.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.#load();
  }

  disconnectedCallback() {
    this.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.#activeFetch?.abort();
  }

  #handleCartAdd = (event) => {
    if (!isMobileBreakpoint() || event.detail?.data?.didError) return;
    this.closest('scroll-hint')?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'data-product-id' || newValue === oldValue || !this.isConnected) return;

    log('product-id changed', { from: oldValue, to: newValue, variant: this.className });

    this.#activeFetch?.abort();

    if (newValue) {
      this.hidden = false;
      this.#load();
    } else {
      const container = this.querySelector('.cart-upsell__cards');
      if (container) container.innerHTML = '';
      this.hidden = true;
    }
  }

  async #load() {
    const { productId, sectionId, limit = '4' } = this.dataset;

    if (!productId || !sectionId) {
      log('skipping load — missing productId or sectionId', { productId, sectionId });
      this.hidden = true;
      return;
    }

    const container = this.querySelector('.cart-upsell__cards');

    // Fade out existing cards before fetching
    if (container && container.children.length > 0) {
      container.classList.add('is-updating');
      await new Promise((r) => setTimeout(r, 180));
    }

    log('loading recs', { productId, sectionId, limit });
    const cards =
      (await this.#fetchRecs(productId, sectionId, 'complementary', limit)) ??
      (await this.#fetchRecs(productId, sectionId, 'related', limit));

    // Guard: product may have changed while we were awaiting
    if (productId !== this.dataset.productId) {
      log('stale response discarded', { fetched: productId, current: this.dataset.productId });
      return;
    }

    if (!container) return;

    if (cards) {
      log('showing', { productId, cardCount: '(injecting now)' });
      container.innerHTML = cards;
      // Remove on next frame to trigger CSS transition fade-in
      requestAnimationFrame(() => container.classList.remove('is-updating'));
      this.hidden = false;
    } else {
      log('no recs found for', productId, '— hiding upsell');
      container.innerHTML = '';
      container.classList.remove('is-updating');
      this.hidden = true;
    }
  }

  /**
   * @param {string} productId
   * @param {string} sectionId
   * @param {'complementary'|'related'} intent
   * @param {string} limit
   * @returns {Promise<string|null>}
   */
  async #fetchRecs(productId, sectionId, intent, limit) {
    const url = `/recommendations/products?product_id=${productId}&section_id=${sectionId}&intent=${intent}&limit=${limit}`;

    if (recCache.has(url)) {
      const cached = recCache.get(url);
      log('cache hit', { url, hasContent: Boolean(cached) });
      return cached || null;
    }

    log('fetching', url);
    try {
      this.#activeFetch?.abort();
      this.#activeFetch = new AbortController();

      const res = await fetch(url, { signal: this.#activeFetch.signal });
      if (!res.ok) {
        log('fetch failed', { status: res.status, url });
        return null;
      }

      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('.cart-upsell-section-content');
      const cards = content?.querySelectorAll('.cart-upsell__card') ?? [];

      log('fetch result', { url, cardCount: cards.length, contentFound: Boolean(content) });

      if (!content || cards.length === 0) {
        recCache.set(url, '');
        return null;
      }

      const result = content.innerHTML;
      recCache.set(url, result);
      return result;
    } catch (err) {
      if (err?.name !== 'AbortError') log('fetch error', err);
      return null;
    } finally {
      this.#activeFetch = null;
    }
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
